import React, { useMemo, useState } from 'react';
import { LockIcon, SearchIcon, UserSearchIcon } from 'lucide-react';
import { useData } from '../../store/DataContext';
import { ConsultationTypeBadge } from '../../components/ui/ConsultationTypeBadge';
import { ConsultationType } from '../../types';
type SearchRow = {
  id: string;
  patientNumber: string;
  fullName: string;
  sex: string;
  contact: string;
  lastVisit: Date | null;
  consultationType?: ConsultationType;
};
const MAX_RESULTS = 50;
const digitsOnly = (value: string) => value.replace(/\D/g, '');

/**
 * Reception PATIENT SEARCH — strictly view-only. Shows basic identification
 * and the most recent visit's consultation type; no clinical data, no actions.
 */
export function PatientSearch() {
  const {
    patients,
    encounters
  } = useData();
  const [query, setQuery] = useState('');

  // Most recent encounter per patient (by visit start time).
  const lastVisitByPatient = useMemo(() => {
    const map = new Map<string, {
      at: Date;
      type?: ConsultationType;
    }>();
    encounters.forEach(e => {
      const at = new Date(e.startedAt);
      if (Number.isNaN(at.getTime())) return;
      const current = map.get(e.patientId);
      if (!current || at > current.at) {
        map.set(e.patientId, {
          at,
          type: e.consultationType
        });
      }
    });
    return map;
  }, [encounters]);
  const results = useMemo<SearchRow[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const qDigits = digitsOnly(q);
    return patients.filter(p => {
      const fullName = `${p.firstName} ${p.lastName}`.toLowerCase();
      return p.bmcFileNumber.toLowerCase().includes(q) || fullName.includes(q) || `${p.lastName} ${p.firstName}`.toLowerCase().includes(q) || qDigits.length >= 3 && digitsOnly(p.contact || '').includes(qDigits);
    }).map(p => {
      const last = lastVisitByPatient.get(p.id);
      return {
        id: p.id,
        patientNumber: p.bmcFileNumber,
        fullName: `${p.firstName} ${p.lastName}`,
        sex: p.sex,
        contact: p.contact || '—',
        lastVisit: last?.at ?? null,
        consultationType: last?.type
      };
    }).sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [patients, lastVisitByPatient, query]);
  const shown = results.slice(0, MAX_RESULTS);
  return <section aria-labelledby="patient-search-heading" className="rounded-xl bg-white p-4 shadow-sm sm:p-6" data-id="element-6103" data-mp="3mjyo1t0r4czy">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3" data-id="element-6104" data-mp="i4fd4pqavpx8">
        <div data-id="element-6105" data-mp="4cincsc3ts40">
          <h2 id="patient-search-heading" className="text-lg font-bold text-gray-900" data-id="element-6106" data-mp="4d0u4iwe7oq6">
            Patient Search
          </h2>
          <p className="text-sm text-gray-500" data-id="element-6107" data-mp="99hfj5c7jozk">
            Look up a patient's basic details and most recent visit.
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600" data-id="element-6108" data-mp="11s4xwcztkpwy">
          <LockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-6109" data-mp="1b55egrh0gzv2" />
          View only
        </span>
      </header>

      <div className="relative" data-id="element-6110" data-mp="174uzwqf5l14l">
        <label htmlFor="patient-search-input" className="sr-only" data-id="element-6111" data-mp="1kw8fxudurguv">
          Search by patient number, full name or phone number
        </label>
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" data-id="element-6112" data-mp="1fyq2prssyfh2" />
        <input id="patient-search-input" type="search" autoFocus autoComplete="off" value={query} onChange={e => setQuery(e.target.value)} placeholder="Patient number, full name or phone number" className="block w-full rounded-lg border border-gray-300 py-3 pl-10 pr-3 text-sm focus:border-chrome-active focus:outline-none focus:ring-1 focus:ring-chrome-active" data-id="element-6113" data-mp="o6r04ml5oefg" />
      </div>

      <div className="mt-5" aria-live="polite" data-id="element-6114" data-mp="28o6wass5s2ev">
        {!query.trim() ? <div className="py-12 text-center text-gray-500" data-id="element-6115" data-mp="i6jrvamgbpim">
            <UserSearchIcon className="mx-auto mb-2 h-10 w-10 text-gray-300" data-id="element-6116" data-mp="n3a3nlr83yy8" />
            <p className="text-sm" data-id="element-6117" data-mp="s3em3hboz3r3">
              Start typing to find a patient.
            </p>
          </div> : shown.length === 0 ? <p className="py-12 text-center text-sm text-gray-500" data-id="element-6118" data-mp="w5jgwjdvm6fb">
            No patients match "{query.trim()}".
          </p> : <>
            <p className="mb-2 text-xs text-gray-500" data-id="element-6119" data-mp="162o3gkbmw3wn">
              {results.length} match{results.length === 1 ? '' : 'es'}
              {results.length > MAX_RESULTS ? ` · showing first ${MAX_RESULTS}, refine your search` : ''}
            </p>
            <div className="overflow-x-auto rounded-lg border border-gray-200" data-id="element-6120" data-mp="1fuk4a353u84k">
              <table className="min-w-full divide-y divide-gray-200 text-sm" data-id="element-6121" data-mp="1js9d343jn6s0">
                <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-600" data-id="element-6122" data-mp="1otyqgqe8nwj9">
                  <tr data-id="element-6123" data-mp="39crbbv560t09">
                    <th scope="col" className="px-4 py-3" data-id="element-6124" data-mp="34cd5mjr1dv5e">Patient No.</th>
                    <th scope="col" className="px-4 py-3" data-id="element-6125" data-mp="34b23eod8ovge">Full Names</th>
                    <th scope="col" className="px-4 py-3" data-id="element-6126" data-mp="30bktniqs5c5f">Sex</th>
                    <th scope="col" className="px-4 py-3" data-id="element-6127" data-mp="2vc88d2fk4d4y">Contact</th>
                    <th scope="col" className="px-4 py-3" data-id="element-6128" data-mp="2ve1gkm5tj5f2">Last Visit</th>
                    <th scope="col" className="px-4 py-3" data-id="element-6129" data-mp="3s1fwmi7k90ck">Consultation Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white" data-id="element-6130" data-mp="3o2q8pvowc0eo">
                  {shown.map(row => <tr key={row.id} data-id="element-6131" data-mp="3eakhio6gxn0j">
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-blue-700" data-id="element-6132" data-mp="3e9huwsg6i6qf">
                        {row.patientNumber}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 font-medium text-gray-900" data-id="element-6133" data-mp="atu59rkamdqe">
                        {row.fullName}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-700" data-id="element-6134" data-mp="tqm9805ez670">
                        {row.sex}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-700" data-id="element-6135" data-mp="ky0v4xcq9noi">
                        {row.contact}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-700" data-id="element-6136" data-mp="oy1i56dw7776">
                        {row.lastVisit ? row.lastVisit.toLocaleDateString('en-GB') : <span className="text-xs font-semibold uppercase text-gray-500" data-id="element-6137" data-mp="fwlb98madn7p">
                            No previous visit
                          </span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3" data-id="element-6138" data-mp="1wcxw95m6s3c8">
                        {!row.lastVisit ? <span className="text-gray-400" data-id="element-6139" data-mp="20f1bm5bz7s2s">—</span> : row.consultationType ? <ConsultationTypeBadge type={row.consultationType} size="sm" data-id="element-6140" data-mp="3kxlljnqcbtb8" /> : <span className="text-xs italic text-gray-500" data-id="element-6141" data-mp="3unevyleqy2dw">
                            Not recorded
                          </span>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </>}
      </div>
    </section>;
}